/* REACT */
import React, { useEffect, useMemo, useState } from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
// import { Drawer } from 'vaul'

/* COMPONENTS */
import { Image, Spinner } from '../../../components/ui'
import ChatSection from './components/ChatSection'

/* STORE */
import {
  fetchChat,
  addPopoverNotificationMessage,
} from '../../../store/maya/actions'
import { setIsPopoverChatOpen } from '../../../store/general/actions'

/* STYLES */
import {
  MayaPopoverWrapper,
  PopoverChatIcon,
  PopoverChatCloseIcon,
  ChatSectionWrapper,
  DrawerOverlay,
  DrawerContent,
  DrawerHandle,
  NotificationContainer,
  NotificationDismissButton,
  CustomPopoverAvatarWrapper,
} from './components/style'

/* UTILS/CONSTANTS/IMAGES-ASSETS */
import {
  size,
  CLOUDFRONT_BASE_URL,
  CLIENT_STYLINGS,
} from '../../../utils/constants'
import {
  createLoadingSelector,
  Analytics,
  getTranslations,
} from '../../../utils'
import { FETCH_CHAT } from '../../../store/maya/constants.js'
import { ChevronDown, X } from 'lucide-react'

const Popover = ({
  language,
  fetchChat,
  organizationSlug,
  slug,
  setIsPopoverChatOpen,
  isPopoverChatOpen,
  isFetchingChat,
  popoverNotificationMessage,
  popoverNotificationMessageDelay,
  popoverNotificationOncePerSession,
  popoverNotificationDuration,
  organizationDetails,
  customPopoverNotificationMessage,
  addPopoverNotificationMessage,
}) => {
  const [isPopoverNotificationVisible, setIsPopoverNotificationVisible] =
    useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  useEffect(() => {
    const allowNotification =
      popoverNotificationMessage?.trim() &&
      popoverNotificationMessageDelay > 0 &&
      !isPopoverChatOpen &&
      !hasUserInteracted &&
      !(
        popoverNotificationOncePerSession &&
        sessionStorage.getItem('maya_notification_shown')
      )

    if (!allowNotification) return

    const delayMs = popoverNotificationMessageDelay * 1000
    const timer = setTimeout(() => {
      if (!hasUserInteracted) {
        setIsPopoverNotificationVisible(true)
        if (popoverNotificationOncePerSession) {
          sessionStorage.setItem('maya_notification_shown', 'true')
        }
      }
    }, delayMs)

    return () => clearTimeout(timer)
  }, [
    popoverNotificationMessage,
    popoverNotificationMessageDelay,
    popoverNotificationOncePerSession,
    isPopoverChatOpen,
    hasUserInteracted,
  ])

  // Auto hide notification after duration
  useEffect(() => {
    if (isPopoverNotificationVisible && popoverNotificationDuration > 0) {
      const hideTimer = setTimeout(() => {
        setIsPopoverNotificationVisible(false)
      }, popoverNotificationDuration * 1000)

      return () => clearTimeout(hideTimer)
    }
  }, [isPopoverNotificationVisible, popoverNotificationDuration])

  const customPopoverAvatar =
    CLIENT_STYLINGS[organizationSlug].POPOVER_AVATAR_URL

  const isLessThanLaptop = useMediaQuery({
    query: `(max-width: ${size.laptop})`,
  })

  const isMobile = useMediaQuery({
    query: `(max-width: ${size.mobileXL})`,
  })

  const getPopoverAvatarDimensions = useMemo(() => {
    let width = '50px'
    let height = '50px'

    if (!isLessThanLaptop) {
      width =
        CLIENT_STYLINGS[organizationSlug].POPOVER_AVATAR_WIDTH_DESKTOP || width
      height =
        CLIENT_STYLINGS[organizationSlug].POPOVER_AVATAR_HEIGHT_DESKTOP ||
        height
    } else {
      width =
        CLIENT_STYLINGS[organizationSlug].POPOVER_AVATAR_WIDTH_MOBILE || width
      height =
        CLIENT_STYLINGS[organizationSlug].POPOVER_AVATAR_HEIGHT_MOBILE || height
    }

    return { width, height }
  }, [isLessThanLaptop, organizationSlug])

  const handlePopoverIconClick = () => {
    // Set custom popover notification message to null
    addPopoverNotificationMessage(null)

    // Mark that user has interacted with the popover
    setHasUserInteracted(true)
    // Hide notification immediately when user interacts
    setIsPopoverNotificationVisible(false)

    const willOpen = !isPopoverChatOpen
    setIsPopoverChatOpen(willOpen)
    Analytics.getInstance().sendEvent('maya_click_button', {
      button_type: 'popover_icon',
      button_subtype: willOpen ? 'open_popover' : 'close_popover',
    })
  }

  const handleDrawerOpenChange = open => {
    // Mark that user has interacted with the popover
    setHasUserInteracted(true)
    // Hide notification immediately when user interacts
    setIsPopoverNotificationVisible(false)

    setIsPopoverChatOpen(open)
    Analytics.getInstance().sendEvent('maya_click_button', {
      button_type: 'popover_icon',
      button_subtype: open ? 'open_popover' : 'close_popover',
    })
  }

  // DISMISS POPUP NOTIFICATION
  const handleDismissNotification = () => {
    setIsPopoverNotificationVisible(false)
    setHasUserInteracted(true)
    addPopoverNotificationMessage(null)
  }

  return (
    <>
      <MayaPopoverWrapper
        id='maya-popover-wrapper'
        $isPopoverChatOpen={isPopoverChatOpen}
        $popoverChatPosition={
          CLIENT_STYLINGS[organizationSlug].POPOVER_CHAT_POSITION
        }
      >
        {customPopoverNotificationMessage ? (
          <NotificationContainer
            $bottom={getPopoverAvatarDimensions.height}
            onClick={handleDismissNotification}
          >
            <NotificationDismissButton onClick={handleDismissNotification}>
              <X size={12} strokeWidth={2.5} />
            </NotificationDismissButton>
            {customPopoverNotificationMessage}
          </NotificationContainer>
        ) : isPopoverNotificationVisible ? (
          <NotificationContainer
            $bottom={getPopoverAvatarDimensions.height}
            onClick={handleDismissNotification}
          >
            <NotificationDismissButton onClick={handleDismissNotification}>
              <X size={12} strokeWidth={2.5} />
            </NotificationDismissButton>
            {popoverNotificationMessage}
          </NotificationContainer>
        ) : null}
        {/* {!isMobile && ( */}
        {
          <>
            {isFetchingChat ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'white',
                  opacity: '0.8',
                  zIndex: '100',
                  position: 'absolute',
                }}
              >
                <Spinner
                  isBasic={false}
                  interval={10000}
                  messages={getTranslations('fetching_chat')}
                />
              </div>
            ) : null}

            <ChatSectionWrapper
              $isOpen={isPopoverChatOpen}
              $enableAiQuickReplies={
                organizationDetails?.configs?.length &&
                organizationDetails?.configs[0]?.enableAiQuickReplies
              }
            >
              <ChatSection
                isLessThanLaptop={isLessThanLaptop}
                language={language}
                isOpen={isPopoverChatOpen}
                isMobile={isMobile}
              />
            </ChatSectionWrapper>
          </>
        }
        {!customPopoverAvatar ? (
          <PopoverChatIcon
            $isOpen={isPopoverChatOpen}
            onClick={handlePopoverIconClick}
          >
            {isPopoverChatOpen ? (
              <PopoverChatCloseIcon>
                <ChevronDown
                  style={{ transform: 'rotate(-45deg) translateY(2px)' }}
                  size={32}
                />
              </PopoverChatCloseIcon>
            ) : (
              <Image
                src={`${CLOUDFRONT_BASE_URL}/assets/common/images/PopoverChatAvatar.svg`}
                alt='Popover Chat Icon'
                width={getPopoverAvatarDimensions.width}
                height={getPopoverAvatarDimensions.height}
              />
            )}
          </PopoverChatIcon>
        ) : (
          <CustomPopoverAvatarWrapper
            $width={getPopoverAvatarDimensions.width}
            $isOpen={isPopoverChatOpen}
            id='popover-avatar-wrapper'
          >
            <Image
              onClick={handlePopoverIconClick}
              src={customPopoverAvatar}
              alt='Popover Chat Icon'
              width={getPopoverAvatarDimensions.width}
              height={getPopoverAvatarDimensions.height}
            />
          </CustomPopoverAvatarWrapper>
        )}
        {/* {isMobile && (
          <div id='debug-drawer-root' style={{ position: 'relative' }}>
            <Drawer.Root
              open={isPopoverChatOpen}
              onOpenChange={handleDrawerOpenChange}
              // snapPoints={[0.85]}
            >
              <DrawerOverlay />
              <DrawerContent>
                <DrawerHandle />
                {isFetchingChat ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'white',
                      opacity: '0.8',
                      zIndex: '100',
                      position: 'absolute',
                    }}
                  >
                    <Spinner
                      isBasic={false}
                      interval={10000}
                      messages={getTranslations('fetching_chat')}
                    />
                  </div>
                ) : null}
                <ChatSection
                  isLessThanLaptop={isLessThanLaptop}
                  isMobile
                  language={language}
                  isOpen={isPopoverChatOpen}
                />
              </DrawerContent>
            </Drawer.Root>
          </div>
        )} */}
      </MayaPopoverWrapper>
    </>
  )
}

const loadingSelector = createLoadingSelector([FETCH_CHAT])

const mapStateToProps = state => ({
  organizationSlug: state.general.organizationSlug,
  isPopoverChatOpen: state.general.isPopoverChatOpen,
  isFetchingChat: loadingSelector(state.loading),
  defaultMessages: state?.maya?.defaultMessages,
  popoverNotificationMessage: state?.maya?.popoverNotificationMessage,
  slug: state?.maya?.slug,
  popoverNotificationMessageDelay: state?.maya?.popoverNotificationMessageDelay,
  popoverNotificationOncePerSession:
    state?.maya?.popoverNotificationOncePerSession,
  popoverNotificationDuration: state?.maya?.popoverNotificationDuration,
  organizationDetails: state?.maya?.organizationDetails,
  customPopoverNotificationMessage:
    state?.maya?.customPopoverNotificationMessage,
})

const mapDispatchToProps = dispatch => ({
  fetchChat: (slug, token) => dispatch(fetchChat(slug, token)),
  setIsPopoverChatOpen: isOpen => dispatch(setIsPopoverChatOpen(isOpen)),
  addPopoverNotificationMessage: message =>
    dispatch(addPopoverNotificationMessage(message)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Popover)
